"use client";

import { IUser } from "@/_services/userServices/auth";
import mixpanel from "mixpanel-browser";
import { useRef } from "react";

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;

export const initMixpanel = () => {
  if (!MIXPANEL_TOKEN) {
    console.warn("Mixpanel token is missing! Check your ev file.");
    return;
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: false,
    api_host: "https://api-eu.mixpanel.com",
    track_pageview: true,
    autocapture: {
      pageview: "url-with-path-and-query-string",
      rage_click: true,
      dead_click: true,
      scroll: true,
      input: true,
      submit: false,
      click: false,
    },
    record_sessions_percent: 25,
    record_heatmap_data: true,
  });
};

export const trackUser = (user: IUser) => {
  try {
    mixpanel.identify(user.userId);
    mixpanel.people.set({
      $email: user.userInfo?.email,
      $name: `${user.userInfo?.firstName} ${user.userInfo?.lastName}`,
      $phone: user.userInfo?.phone,
      $company: user.company,
      country: user.country,
    });
  } catch (error) {
    console.warn("Mixpanel trackUser failed:", error);
  }
};

export enum BIEvents {
  LOGIN_SUBMIT = "login_submit",
  REGISTER_CANDIDATE_SUB = "register_candidate_submit",
  REGISTER_RECRUITER_SUB = "register_recruiter_submit",
  LOGIN_WITH_GOOGLE = "login_with_google",
  LOGIN_WITH_LINKEDIN = "login_with_linkedin",
  LOGOUT = "logout",
  QUICK_SEARCH = "quick_search",
  CLEAR_QUICK_SEARCH = "clear_quick_search",
  SAVE_JOB_DEFINITION_BASIC_INFO = "save_job_definition_basic_info",

  OAUTH_ERROR = "oauth_error",
  OAUTH_SUCCESS = "oauth_success",
  OAUTH_BACK_TO_LOGIN = "oauth_back_to_login",
  CHECK_SUBSCRIPTION = "check_subscription",
  SWITCH_SEARCH_MODE = "switch_search_mode",
  START_SEARCH = "start_search",

  TALENT_POOL_UPLOAD_POPOVER_OPEN = "talent_pool_upload_popover_open",
  TALENT_POOL_UPLOAD_FILE_DIALOG_OPEN = "talent_pool_upload_file_dialog_open",
  TALENT_POOL_NEW_GROUP = "talent_pool_new_group",
  TALENT_POOL_SORT_BY_NAME = "talent_pool_sort_by_name",
  TALENT_POOL_SORT_BY_SIZE = "talent_pool_sort_by_size",
  TALENT_POOL_SORT_BY_LATEST = "talent_pool_sort_by_latest",
  TALENT_POOL_STAY_ON_PAGE = "talent_pool_stay_on_page",
  TALENT_POOL_SEARCH = "talent_pool_search",
  TALENT_POOL_FILTER_STATUS = "talent_pool_filter_status",
  TALENT_POOL_VIEW_MODE = "talent_pool_view_mode",

  BLOG_OPEN_ARTICLE = "blog_open_article",
  BLOG_STAY_ON_ARTICLE = "blog_stay_on_article",
  BLOG_STAY_ON_LISTING = "blog_stay_on_listing",

  PRICING_STAY_ON_PAGE = "pricing_stay_on_page",
  PRICING_SELECT_PLAN = "pricing_select_plan",
  PRICING_BILLING_TOGGLE = "pricing_billing_toggle",
  PRICING_VN_BANNER_EMAIL = "pricing_vn_banner_email",
  PRICING_VN_BANNER_FANPAGE = "pricing_vn_banner_fanpage",

  BENCHMARK_START = "benchmark_start",
  BENCHMARK_RESULT = "benchmark_result",
  BENCHMARK_ERROR = "benchmark_error",

  APPLY_PAGE_VIEW = "apply_page_view",
  APPLY_UPLOAD_CV = "apply_upload_cv",
  APPLY_SUBMIT = "apply_submit",
}

export const trackEvent = (
  event: BIEvents,
  metadata: Record<string, unknown>,
) => {
  try {
    mixpanel.track(event, metadata);
  } catch (error) {
    console.warn("Mixpanel trackEvent failed:", error);
  }
};

export const useTimingEvent = (
  event: BIEvents,
  metadata: Record<string, unknown>,
) => {
  const canStopRef = useRef(false);
  return {
    startTrack: () => {
      canStopRef.current = true;
      try {
        mixpanel.time_event(event);
      } catch (error) {
        console.warn("Mixpanel time_event failed:", error);
        canStopRef.current = false;
      }
    },
    stopTrack: () => {
      if (canStopRef.current) {
        try {
          mixpanel.track(event, metadata);
        } catch (error) {
          console.warn("Mixpanel trackEvent failed:", error);
        }
        canStopRef.current = false;
      }
    },
  };
};
